import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "set-up-schemas-and-datasets"
    }}>{`Set up schemas and datasets`}</h1>
    <p>{`To standardize data collection for use across applications that leverage Adobe Experience Platform, Adobe has created the open and publicly documented standard, `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/xdm/home.html"
      }}>{`Experience Data Model (XDM)`}</a>{`.`}</p>
    <p>{`XDM provides a foundational framework to allow `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/landing/home.html"
      }}>{`Platform`}</a>{` services and Adobe Experience Cloud solutions to interoperate for reliable marketing and experience delivery use cases.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`To learn more about XDM, please read the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/xdm/home.html"
      }}>{`XDM system overview`}</a>{`​`}</p>
    <p>{`To leverage data collection via the Experience Platform Edge Network, data has to be sent in as XDM objects. The use of non-XDM objects is currently unsupported.`}</p>
    <p>{`In order to begin sending XDM data from your mobile application to Edge Network, you'll need to do the following:`}</p>
    <ol>
      <li parentName="ol"><a parentName="li" {...{
          "href": "#create-and-configure-your-xdm-schema"
        }}>{`Create and configure your XDM schema`}</a></li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "#create-and-configure-a-dataset"
        }}>{`Create and configure your dataset based on the previously created schema`}</a></li>
    </ol>
    <h2 {...{
      "id": "create-and-configure-your-xdm-schema"
    }}>{`Create and configure your XDM schema`}</h2>
    <h3 {...{
      "id": "what-is-an-xdm-schema"
    }}>{`What is an XDM schema?`}</h3>
    <p>{`Schemas are a formalized description of the structure of your data. Schemas provide a standardized way of describing data in Experience Platform. This allows for all data that conforms to schemas to be reused across an organization without conflicts, or even shared between multiple organizations.`}</p>
    <p>{`Edge Network and Adobe Experience Platform require your incoming data to have a defined schema that describes your data’s structure and provide constraints to the type of data that can be contained within each field.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`To learn more about creating your own XDM schema, industry specific recommendations, or best practices, please read the document on the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/xdm/schema/composition.html"
      }}>{`basics of schema composition`}</a>{`.`}</p>
    <h3 {...{
      "id": "creating-a-sample-schema"
    }}>{`Creating a sample schema`}</h3>
    <p>{`The following steps illustrate the creation of a sample schema for mobile data collection:`}</p>
    <ol>
      <li parentName="ol">{`In your browser, navigate to `}<a parentName="li" {...{
          "href": "https://experience.adobe.com/platform"
        }}>{`Adobe Experience Platform`}</a>{` and login with your credentials.`}</li>
      <li parentName="ol">{`Create an XDM Schema as follows:`}
        <ol parentName="li">
          <li parentName="ol">{`From the left panel, select Schemas`}</li>
          <li parentName="ol">{`Select `}<strong parentName="li">{`Create schema`}</strong>{`.`}</li>
          <li parentName="ol">{`Select `}<strong parentName="li">{`XDM Experience event.`}</strong></li>
          <li parentName="ol">{`Under `}<strong parentName="li">{`Field Groups`}</strong>{`, select `}<strong parentName="li">{`Add`}</strong>{`; search and add the `}<inlineCode parentName="li">{`Adobe Experience Edge Autofilled Environment Details`}</inlineCode>{`, `}<inlineCode parentName="li">{`AEP Mobile Lifecycle Details`}</inlineCode>{` and other pre-created or custom field groups as needed.`}</li>
          <li parentName="ol">{`Set a `}<strong parentName="li">{`Display Name`}</strong>{` for this schema and select `}<inlineCode parentName="li">{`Save`}</inlineCode>{`.`}</li>
        </ol>
      </li>
    </ol>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "59.68750000000001%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/5530d/create-schema.webp 320w", "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/0c8fb/create-schema.webp 640w", "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/94b1e/create-schema.webp 1280w", "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/0b34d/create-schema.webp 1920w", "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/a8ede/create-schema.webp 2272w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/dd4a7/create-schema.png 320w", "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/0f09e/create-schema.png 640w", "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/bbbf7/create-schema.png 1280w", "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/ac7a9/create-schema.png 1920w", "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/228fd/create-schema.png 2272w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/c073d1de567768d0e6e1244a7e01dd78/bbbf7/create-schema.png",
            "alt": "Schema creation in Adobe Experience Platform",
            "title": "Schema creation in Adobe Experience Platform",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <h2 {...{
      "id": "create-and-configure-a-dataset"
    }}>{`Create and configure a dataset`}</h2>
    <p>{`Data ingested into Adobe Experience Platform is stored (in the Data Lake) as datasets that conform to the aforementioned mentioned XDM schema (setup in the step above).`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`To learn more about datasets in Adobe Experience Platform, please read the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/experience-platform/catalog/datasets/overview.html"
      }}>{`datasets overview`}</a>{`.`}</p>
    <p>{`The following steps illustrate the creation of a sample dataset for mobile data collection:`}</p>
    <ol>
      <li parentName="ol">{`In the browser, navigate to `}<a parentName="li" {...{
          "href": "https://experience.adobe.com/platform"
        }}>{`Adobe Experience Platform`}</a>{` and login with your credentials.`}</li>
      <li parentName="ol">{`Create a new dataset as follows:`}
        <ol parentName="li">
          <li parentName="ol">{`From the left navigation panel, under `}<strong parentName="li">{`Data Management`}</strong>{`, select `}<strong parentName="li">{`Datasets`}</strong>{`.`}</li>
          <li parentName="ol">{`Select `}<strong parentName="li">{`Create dataset from schema`}</strong>{`.`}</li>
          <li parentName="ol">{`Select the XDM schema previously created and select `}<strong parentName="li">{`Next`}</strong>{`.`}</li>
          <li parentName="ol">{`Set a name for the dataset and select `}<strong parentName="li">{`Finish`}</strong>{`.`}</li>
        </ol>
      </li>
    </ol>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "1280px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "51.25000000000001%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/5530d/create-dataset.webp 320w", "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/0c8fb/create-dataset.webp 640w", "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/94b1e/create-dataset.webp 1280w", "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/0b34d/create-dataset.webp 1920w", "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/a552e/create-dataset.webp 2268w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/dd4a7/create-dataset.png 320w", "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/0f09e/create-dataset.png 640w", "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/bbbf7/create-dataset.png 1280w", "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/ac7a9/create-dataset.png 1920w", "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/7a2a7/create-dataset.png 2268w"],
            "sizes": "(max-width: 1280px) 100vw, 1280px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/client-sdks/static/41ea703f5460c42fa16b91c6e6c4692b/bbbf7/create-dataset.png",
            "alt": "Dataset creation in Adobe Experience Platform",
            "title": "Dataset creation in Adobe Experience Platform",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      